import { Paper } from '@snowball-tech/fractal/dist/src/components/Paper'
import { cj, cn } from '@snowball-tech/fractal/dist/src/styles/helpers'
import Image, { type StaticImageData } from 'next/image'

import type { ComponentProps } from 'react'

import isEmpty from 'lodash/fp/isEmpty'

import { ChildrenProps } from '../types'
import Link from './Link'

type Props = {
  className?: string
  condensed?: boolean
  elevation?: '0' | ComponentProps<typeof Paper>['elevation']
  fullWidth?: boolean
  href?: string
  id?: string
  image?: string | StaticImageData
  imageClassName?: string
  orientation?: 'horizontal' | 'vertical'
  rootClassName?: string
  variant?: 'brutal' | 'subtle'
} & ChildrenProps
export default function Card({
  children,
  className,
  condensed = false,
  elevation = '2',
  fullWidth = false,
  href,
  // variant = 'brutal',
  id,
  image,
  imageClassName,
  orientation = 'horizontal',
  rootClassName,
}: Props) {
  const asLink = !isEmpty(href)

  const card = (
    <Paper
      id={asLink ? undefined : id}
      className={cn(
        'flex p-0',
        orientation === 'vertical'
          ? 'h-full w-[320px] min-w-[320px] max-w-[320px] flex-col'
          : 'h-full min-h-15 w-full min-w-full max-w-screen-md flex-row items-stretch',
        condensed && orientation === 'horizontal' ? '' : 'md:min-h-[212px]',
        fullWidth ? 'w-full min-w-full max-w-full' : '',
        elevation === '0'
          ? 'shadow-none border-1 group-hover:shadow-hover group-active:shadow-none'
          : 'border-2 group-hover:shadow-brutal group-active:shadow-none',
        className,
        asLink ? '' : 'group',
        asLink ? '' : rootClassName,
      )}
      elevation={elevation === '0' ? '1' : elevation}
    >
      {image && (
        <div
          className={cn(
            'p-quarter',
            orientation === 'vertical'
              ? 'h-[240px] w-full rounded-t-sm border-b-2'
              : 'w-15 max-w-15 rounded-l-sm border-r-2',
            condensed && orientation === 'horizontal'
              ? ''
              : 'md:w-unset md:min-w-[200px] md:max-w-unset',
            imageClassName,
          )}
        >
          <div
            className={cj(
              'size-full relative bg-body',
              orientation === 'vertical'
                ? 'rounded-t-[6px]'
                : 'rounded-l-[6px]',
            )}
          >
            <Image
              alt=""
              className={cj(
                'object-cover',
                orientation === 'vertical'
                  ? 'rounded-t-[6px]'
                  : 'rounded-l-[6px]',
              )}
              fill
              loading="eager"
              priority
              sizes="320px"
              src={image}
            />
          </div>
        </div>
      )}

      <div
        className={cj(
          'flex flex-col gap-2 p-2 text-left flex-1',
          orientation === 'vertical'
            ? 'h-full rounded-b-sm'
            : 'w-full rounded-r-sm',
        )}
      >
        {children}
      </div>
    </Paper>
  )

  return asLink ? (
    <Link
      id={id}
      className={cn('group w-full no-underline', rootClassName)}
      href={href!}
    >
      {card}
    </Link>
  ) : (
    card
  )
}
